import React from "react";
import styles from "./styles.module.css";
import { getRegExp, getSentenceFromCamelCase } from "./Helpers";
import {
  FaAt,
  FaCalendarAlt,
  FaClock,
  FaEnvelope,
  FaFileAlt,
  FaGlobe,
  FaHashtag,
  FaIdBadge,
  FaLink,
  FaMusic,
  FaPhoneAlt,
  FaSearch,
  FaTag,
  FaUpload,
  FaUser,
} from "react-icons/fa";
import { InputProps } from "../../types/form";

const Input = ({
  checked,
  className,
  disabled,
  error,
  fixLength,
  required,
  label,
  minLength,
  maxLength,
  name,
  onChange,
  placeholder,
  reqType,
  style,
  type = "text",
  validationHandler,
  value,
  size = "medium",
  labelClassName,
}: InputProps) => {
  const iconMap: Record<string, JSX.Element> = {
    email: <FaEnvelope />,
    password: <FaAt />,
    tel: <FaPhoneAlt />,
    phone: <FaPhoneAlt />,
    number: <FaHashtag />,
    date: <FaCalendarAlt />,
    time: <FaClock />,
    file: <FaUpload />,
    search: <FaSearch />,
    url: <FaLink />,
    website: <FaGlobe />,
    artist: <FaUser />,
    title: <FaMusic />,
    name: <FaIdBadge />,
    category: <FaTag />,
    text: <FaFileAlt />,
  };

  const semanticKey = name?.toLowerCase?.() || type;
  const icon = iconMap[semanticKey] || iconMap[type] || <FaFileAlt />;
  const mappedClassName = className && styles[className] ? styles[className] : className || "";

  const onChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked, files } = event.target;
    const inputValue = type === "file" ? files?.[0] : type === "checkbox" ? checked : value;
    onChange && onChange(name, inputValue as any);
  };

  const onValidationChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!validationHandler) return;
    const { value } = event.target;
    let errorMessage = "";

    if (!value && required) {
      errorMessage = `Please enter ${getSentenceFromCamelCase(name)}.`;
    } else if (minLength && value.length < minLength) {
      errorMessage = `${
        name.charAt(0).toUpperCase() + getSentenceFromCamelCase(name).slice(1)
      } must be at least ${minLength} characters long.`;
    } else if (maxLength && value.length > maxLength) {
      errorMessage = `${
        name.charAt(0).toUpperCase() + getSentenceFromCamelCase(name).slice(1)
      } must be ${maxLength} characters long.`;
    } else if (fixLength && value.length !== fixLength) {
      errorMessage = `${
        name.charAt(0).toUpperCase() + getSentenceFromCamelCase(name).slice(1)
      } must be ${fixLength} characters.`;
    } else if (value && reqType) {
      const regExp = getRegExp(reqType) as RegExp;
      if (!regExp.test(value)) {
        errorMessage = `Please enter valid ${getSentenceFromCamelCase(name)}.`;
      }
    }

    validationHandler(name, errorMessage);
  };

  return (
    <div className={`${styles.InputContainer} ${styles[size || "medium"]}`}>
      <div className={styles.labelRow}>
        <label htmlFor={name} className={`${styles.label} ${labelClassName || ""}`}>
          {label}
          {required && <span className={styles.required}>&nbsp;*</span>}
        </label>
        {error ? <span className={styles.errorText}>{error}</span> : null}
      </div>

      <div className={`${styles.fieldWrapper} ${error ? styles.hasError : ""} ${disabled ? styles.disabled : ""}`}>
        <span className={styles.inputIcon}>{icon}</span>
        <input
          type={type}
          name={name}
          id={name}
          value={type === "file" ? "" : ((value as any) || "")}
          checked={type === "checkbox" ? checked : undefined}
          placeholder={placeholder || `Enter ${label?.toLowerCase?.() || getSentenceFromCamelCase(name)}`}
          className={`${styles.inputControl} ${styles.blackBorder} ${mappedClassName}`}
          required={required}
          style={style}
          disabled={disabled}
          onChange={onChangeHandler}
          onBlur={onValidationChange}
        />
      </div>
    </div>
  );
};

export default Input;
